import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import {
  Comment,
  GetCommentParams,
  GetCommentsResponse,
  ID,
  PostCommentResponse,
} from '../../api/comments';
import { authSelector } from '../../store';
import { notifyUser } from '../../store/Toast';
import { getElapsedTime, getInitials } from '../../utils';
import AccentTitle from '../UI/AccentTitle';
import ImageCircularAvatar from '../UI/Avatar/ImageCircularAvatar';
import { HookForm } from '../UI/HookForm/Form';
import { HookTextarea } from '../UI/HookForm/Form/TextArea';
import { HookFormProvider } from '../UI/HookForm/Provider';
import { LoadingComments } from '../UI/loading/LoadingComments';

export interface CommentsProps {
  getComments: (
    params: GetCommentParams
  ) => Promise<GetCommentsResponse<Comment>>;
  postComments: (values: {
    comment: string;
  }) => Promise<PostCommentResponse<ID>>;
  queryKey: string;
  dependency?: readonly any[];
}

function CommentsBase(props: CommentsProps) {
  const { getComments, postComments, queryKey, dependency = [] } = props;
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { reset } = useFormContext();
  const { data: currentUserData } = useSelector(authSelector);
  const [pagination, setPagination] = useState<{
    page: number;
    perPage: number;
  }>({ page: 1, perPage: 3 });
  const { page, perPage } = pagination;

  const fetchComments = useQuery([queryKey, ...dependency], () =>
    getComments({ page, perPage })
  );
  const comments = fetchComments?.data;

  const { mutate, isLoading } = useMutation(postComments, {
    onSuccess: ({ data }) => {
      reset({ comment: '' });
      queryClient.setQueryData<GetCommentsResponse<Comment>>(
        [queryKey, ...dependency],
        (oldQueryData: any) => {
          return {
            ...oldQueryData,
            data: [...oldQueryData.data, data],
            meta: { total: oldQueryData.meta.total + 1 },
          };
        }
      );
    },
    onError: (previousValue) => {
      queryClient.setQueryData([queryKey, ...dependency], previousValue);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: `Failed to comment`,
          toastType: 'failed',
        })
      );
    },
  });

  comments?.data?.sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();
    return dateA > dateB ? 1 : -1;
  });

  return (
    <section aria-labelledby='notes-title'>
      <div className='bg-white shadow sm:rounded-lg sm:overflow-hidden'>
        <div className='divide-y divide-gray-200'>
          <AccentTitle title={`Comments (${comments?.meta?.total || 0})`} />
          {fetchComments.isLoading && <LoadingComments />}
          {comments && comments?.meta?.total < 0 && (
            <p className='p-6'>No Comments</p>
          )}
          {comments && comments?.meta?.total > 0 && (
            <div className='px-4 py-6 sm:px-6'>
              {comments?.meta?.total > pagination.perPage && (
                <div className='flex justify-center mb-5'>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setPagination((prev) => ({
                        ...prev,
                        perPage: perPage + 3,
                      }));
                    }}
                    className='inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md text-green-600 bg-white hover:text-green-700'
                  >
                    Load previous comments
                  </button>
                </div>
              )}
              <ul className='space-y-8'>
                {comments?.data?.map((comment) => (
                  <li key={comment.id}>
                    <div className='flex space-x-3'>
                      <div className='flex-shrink-0'>
                        <ImageCircularAvatar
                          imageUrl={comment?.user?.profileImageUrl}
                          fallbackText={getInitials(comment?.user?.name)}
                        />
                      </div>
                      <div>
                        <div className='text-sm'>
                          <p className='font-medium text-gray-900'>
                            {comment.user.name}
                          </p>
                        </div>
                        <div className='mt-1 text-sm text-gray-700'>
                          <p>{comment.comment}</p>
                        </div>
                        <div className='mt-2 text-xs space-x-2'>
                          <span className='text-gray-500 font-medium'>
                            {getElapsedTime(
                              new Date(comment.createdAt).getTime() / 1000
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className='bg-gray-50 px-4 py-6 sm:px-6'>
          <div className='flex space-x-3'>
            <div className='flex-shrink-0'>
              <ImageCircularAvatar
                imageUrl={currentUserData?.profileImageUrl}
                fallbackText={getInitials(currentUserData.name)}
              />
            </div>
            <div className='min-w-0 flex-1'>
              <HookForm onSubmit={mutate}>
                <div>
                  <HookTextarea
                    id='comment'
                    name='comment'
                    label='About'
                    disabled={isLoading}
                    srOnlyLabel
                    rows={3}
                    className='p-2 shadow-sm block w-full sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primaryColor-normal'
                    placeholder='Post comment'
                  />
                </div>
                <div className='mt-3 flex items-center justify-between'>
                  <button
                    type='submit'
                    disabled={isLoading}
                    className='inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700'
                  >
                    {isLoading ? 'Please wait...' : 'Post'}
                  </button>
                </div>
              </HookForm>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Comments(props: CommentsProps) {
  return (
    <HookFormProvider>
      <CommentsBase {...props} />
    </HookFormProvider>
  );
}
